import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { SEOHead, JsonLd } from "@/components/SEO";
import { VendorCard, ReviewSnippet } from "@/pages/seo/_components";
import { Stethoscope, MapPin, Calendar } from "lucide-react";

/**
 * /vendors/{city-slug}/{category-slug} — e.g. /vendors/washington-dc/chiropractor
 *
 * The HIGHEST SEO priority page in the app. Owns long-tail intent like
 * "chiropractor health fair washington dc". Renders: hyper-specific h1 +
 * description, the matching vendors, other categories in the same city
 * (internal-link tower), upcoming events, and recent reviews.
 */
export default function VendorCategoryCityPage() {
    const { citySlug, categorySlug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/seo/cities/${citySlug}/${categorySlug}`);
                if (!cancelled) setData(r.data);
            } catch (e) { if (!cancelled) setErr(e); }
        })();
        return () => { cancelled = true; };
    }, [citySlug, categorySlug]);

    if (err) return <Shell><div className="p-10 text-brand-mute">Failed to load.</div></Shell>;
    if (!data) return <Shell><div className="p-10 text-brand-mute">Loading…</div></Shell>;

    const loc = data.location;
    const cat = data.category;
    const cityLabel = `${loc.city}${loc.state ? `, ${loc.state}` : ""}`;
    const n = data.vendor_count;
    const titleCore = `${cat.name} vendors in ${cityLabel}`;
    const description = `${n} ${cat.name.toLowerCase()} vendor${n === 1 ? "" : "s"} on HealthFairX serving ${cityLabel}-area corporate health fairs. Verified host reviews, transparent booth fees, and instant booking.`;

    const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin : "" },
            { "@type": "ListItem", position: 2, name: "Vendors", item: typeof window !== "undefined" ? `${window.location.origin}/vendors` : "" },
            { "@type": "ListItem", position: 3, name: cityLabel, item: typeof window !== "undefined" ? `${window.location.origin}/vendors/${loc.slug}` : "" },
            { "@type": "ListItem", position: 4, name: cat.name },
        ],
    };
    const itemList = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: titleCore,
        numberOfItems: n,
        itemListElement: data.vendors.slice(0, 25).map((v, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: typeof window !== "undefined" ? `${window.location.origin}/vendors/${v.vendor_slug || v.id}` : undefined,
            name: v.business_name,
        })),
    };

    return (
        <Shell>
            <SEOHead title={titleCore} description={description} />
            <JsonLd data={breadcrumb} />
            <JsonLd data={itemList} />

            <section className="bg-brand-bone border-b border-brand-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
                    <div className="text-xs uppercase tracking-wider text-brand-mute font-bold flex items-center gap-2 flex-wrap">
                        <Link to="/vendors" className="hover:text-brand-forest">Vendors</Link>
                        <span>/</span>
                        <Link to={`/vendors/${loc.slug}`} className="hover:text-brand-forest">{cityLabel}</Link>
                        <span>/</span>
                        <span className="text-brand-ink">{cat.name}</span>
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-3 flex items-center gap-3" data-testid="seo-citycat-h1">
                        <Stethoscope size={36} className="text-brand-forest" /> {cat.name} vendors in {cityLabel}
                    </h1>
                    <p className="mt-3 text-brand-mute max-w-3xl">
                        {n > 0
                            ? `${n} ${cat.name.toLowerCase()} vendor${n === 1 ? "" : "s"} serving ${cityLabel}-area corporate health fairs. Vetted by HealthFairX and rated by hosts you can trust.`
                            : <>No {cat.name.toLowerCase()} vendors are listed in {cityLabel} yet. Try <Link to={`/vendors/${cat.slug}`} className="text-brand-forest font-semibold underline">{cat.name.toLowerCase()} vendors nationwide</Link> or <Link to={`/vendors/${loc.slug}`} className="text-brand-forest font-semibold underline">all categories in {cityLabel}</Link>.</>}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-12">
                {/* Vendors */}
                {data.vendors.length > 0 && (
                    <section data-testid="seo-citycat-vendors">
                        <h2 className="font-heading text-2xl font-bold">{cat.name} vendors in {cityLabel}</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
                            {data.vendors.map((v) => <VendorCard key={v.id} v={v} testid={`seo-citycat-vendor-${v.id}`} />)}
                        </div>
                    </section>
                )}

                {/* Other categories in this city — internal link tower */}
                {data.other_categories_in_city.length > 0 && (
                    <section data-testid="seo-citycat-other">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <MapPin size={20} className="text-brand-forest" /> Other vendor categories in {cityLabel}
                        </h2>
                        <div className="flex flex-wrap gap-2 mt-4">
                            {data.other_categories_in_city.map((c) => (
                                <Link key={c.id} to={`/vendors/${loc.slug}/${c.slug}`}
                                      data-testid={`seo-citycat-other-${c.slug}`}
                                      className="pill px-4 py-2 text-sm font-semibold bg-white border border-brand-line hover:border-brand-forest hover:bg-brand-mint">
                                    {c.name} <span className="text-xs text-brand-mute ml-1.5">({c.vendor_count})</span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Upcoming events */}
                {data.upcoming_events.length > 0 && (
                    <section data-testid="seo-citycat-events">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <Calendar size={20} className="text-brand-forest" /> Upcoming health fairs in {cityLabel}
                        </h2>
                        <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                            {data.upcoming_events.map((ev) => (
                                <li key={ev.event_id} className="rounded-xl border border-brand-line bg-white p-4">
                                    <Link to={`/events/${ev.event_slug || ev.event_id}`} className="font-semibold hover:underline block">{ev.name}</Link>
                                    <div className="text-xs text-brand-mute mt-1">{ev.event_date}</div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Recent reviews */}
                {data.recent_reviews.length > 0 && (
                    <section data-testid="seo-citycat-reviews">
                        <h2 className="font-heading text-2xl font-bold">Recent reviews</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
                            {data.recent_reviews.map((r) => <ReviewSnippet key={r.id} r={r} />)}
                        </div>
                    </section>
                )}
            </div>
        </Shell>
    );
}
